import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useUppyEvent, Dashboard } from '@uppy/react';
import {
  Grid, Segment, Header, Button,
} from 'semantic-ui-react';
import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import '@uppy/status-bar/dist/style.min.css';
import { clientErrors, translatedError } from '@/utils/errors';
import fields from '@/constants/fields';
import { uppyBaseOptions, useUppy } from '@/utils/uppy';
import ColumnValidationError from '@/components/errors/ColumnValidationError';
import { mapStateToProps, mapDispatchToProps } from '../components/DatasetConditionContainer';
import UserDefinedTextRestrictionEditor, {
  validateUserDefinedTextRestriction,
} from './UserDefinedTextRestrictionEditor';
import { restrictionAttachmentParams } from '../constants';
import { restrictionsAttachmentsMap } from '../helper';
import UserDefinedAttachmentIcon from '../components/UserDefinedAttachmentIcon';
import UserDefinedAttachmentLink from './UserDefinedAttachmentLink';

export const validateUserDefinedAttachmentRestriction = (datasetCondition, errors, isNew) => {
  if ((isNew || !datasetCondition?.context?.attachmentName) && !datasetCondition.context.attachmentMapId) {
    errors.push(translatedError(
      'dataset.condition',
      'dataset.condition.validation.invalid.title',
      'validation.attachment.required',
      [fields.ATTACHMENT],
    ));
  }

  validateUserDefinedTextRestriction(datasetCondition, errors, false);
};

export const useAttachmentUppy = (datasetCondition, updateProperties) => {
  const [uppyId] = React.useState(
    `uppy-restriction-attachment-${datasetCondition?.id || datasetCondition?.condition?.code}`,
  );
  const options = React.useMemo(
    () => ({
      id: uppyId,
    }),
    [uppyId],
  );

  const baseOptions = React.useMemo(
    () => ({
      ...uppyBaseOptions,
      autoProceed: false,
      allowMultipleUploads: false,
      restrictions: {
        maxFileSize: restrictionAttachmentParams.maxFileSize,
        minFileSize: 1,
        maxTotalFileSize: restrictionAttachmentParams.maxFileSize,
        maxNumberOfFiles: 1,
        allowedFileTypes: restrictionAttachmentParams.allowedExtensions,
      },
    }),
    [uppyId],
  );

  const uppy = useUppy(baseOptions, options);

  useUppyEvent(uppy, 'file-added', (file) => {
    restrictionsAttachmentsMap.set(uppyId, file.data);

    updateProperties(
      {
        attachmentMapId: uppyId,
        attachmentName: file.name,
        attachmentSize: file.size,
        attachmentType: file.type,
      },
      fields.ATTACHMENT,
    );
  });

  useUppyEvent(uppy, 'file-removed', () => {
    updateProperties(
      {
        attachmentMapId: undefined,
        attachmentName: undefined,
        attachmentSize: undefined,
        attachmentType: undefined,
      },
      fields.ATTACHMENT,
    );

    restrictionsAttachmentsMap.delete(uppyId);
  });

  React.useEffect(
    () => () => {
      uppy.cancelAll();
    },
    [uppy],
  );

  return uppy;
};

const UserDefinedAttachmentRestrictionEditor = ({
  datasetCondition,
  updateProperties,
  saveErrors,
  category,
  code,
  isDownloading,
}) => {
  const intl = useIntl();
  const uppy = useAttachmentUppy(datasetCondition, updateProperties);
  const attachmentError = clientErrors(saveErrors).find((e) => e.fields.includes(fields.ATTACHMENT));

  return (
    <>
      <Grid columns="equal" className="mb-2">
        <Grid.Row>
          <ColumnValidationError
            error={attachmentError}
            htmlFor="ADDUC_0005_attachment"
            labelId="dataset.condition.restriction.attachment.label"
          >
            {datasetCondition?.context?.attachmentName ? (
              <Segment placeholder style={{ position: 'relative' }}>
                <Header icon>
                  <UserDefinedAttachmentIcon outline datasetCondition={datasetCondition} isLoading={isDownloading} />
                  <UserDefinedAttachmentLink code={code} category={category} datasetCondition={datasetCondition}>
                    {datasetCondition.context.attachmentName}
                  </UserDefinedAttachmentLink>
                </Header>
                <Button
                  basic
                  circular
                  icon="close"
                  className="absolute top-5 right-5"
                  onClick={() => {
                    updateProperties(
                      {
                        attachmentMapId: undefined,
                        attachmentName: undefined,
                        attachmentSize: undefined,
                        attachmentType: undefined,
                      },
                      fields.ATTACHMENT,
                    );

                    uppy.cancelAll();
                  }}
                />
              </Segment>
            ) : (
              <Dashboard
                id="ADDUC_0005_attachment"
                className="aridhia-uppy-dashboard attachments-restrictions"
                uppy={uppy}
                proudlyDisplayPoweredByUppy={false}
                hideUploadButton
                height={252}
                disableStatusBar
                disableThumbnailGenerator
                note={intl.formatMessage({ id: 'upload.dashboard.drop.restrictions.attachments' })}
              />
            )}
          </ColumnValidationError>
        </Grid.Row>
      </Grid>
      <UserDefinedTextRestrictionEditor />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UserDefinedAttachmentRestrictionEditor);
